import { DEFAULT_LOCALE, LOCALES, type Locale } from "./locales";
import { en } from "./dictionaries/en";
import { es } from "./dictionaries/es";
import { de } from "./dictionaries/de";
import { fr } from "./dictionaries/fr";
import { it } from "./dictionaries/it";
import { ru } from "./dictionaries/ru";
import { ptBr } from "./dictionaries/pt-br";
import { ar } from "./dictionaries/ar";

export type Bundle = Record<string, string>;

/**
 * Core (shell-wide) strings ship with the main bundle — they are small and every
 * render needs them. Page-level dictionaries are far larger, so they are loaded
 * per locale: eagerly on the server (see pages.server.ts) and as a single
 * per-locale chunk on the client (see client.tsx), instead of shipping all eight
 * languages to every mobile visitor.
 */
const CORE: Record<Locale, Bundle> = {
  en,
  es,
  de,
  fr,
  it,
  ru,
  "pt-br": ptBr,
  ar,
} as Record<Locale, Bundle>;

export const DICTIONARIES: Record<Locale, Bundle> = LOCALES.reduce(
  (acc, locale) => {
    acc[locale] = { ...CORE[locale] };
    return acc;
  },
  {} as Record<Locale, Bundle>,
);

/** Merge a page dictionary into the active registry for one locale. */
export function registerMessages(locale: Locale, bundle: Bundle | undefined) {
  if (!bundle || !DICTIONARIES[locale]) return;
  Object.assign(DICTIONARIES[locale], bundle);
}

export function translate(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const value = DICTIONARIES[locale]?.[key] ?? DICTIONARIES[DEFAULT_LOCALE]?.[key] ?? key;
  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}
